import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AXES,
  type AxisId,
  type ComponentPlacement,
  type CutPiece,
  type PipeRun,
  type RunFitting,
  type SketchNote,
  type SupportPlacement,
  type TerminationPlacement,
  type Vec3,
} from "@/hooks/use-pipe-sketch";
import {
  formatCoords,
  formatElevation,
  formatFtIn,
  getComponentSpec,
  getMaterial,
  getSizeSpec,
  getSupportSpec,
  getTerminationSpec,
  type ComponentKind,
  type MaterialId,
  type SupportKind,
  type TerminationKind,
} from "@/lib/pipe-spec";
import ComponentSymbol from "./ComponentSymbol";
import { SupportSymbol, TerminationSymbol } from "./SupportSymbol";

const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;
/** drawing snap increment, inches */
const SNAP = 3;
/** background grid spacing, inches */
const GRID = 12;
/** pointer distance (px) for snapping a branch onto an existing run */
const BRANCH_HIT = 14;

export interface Screen {
  x: number;
  y: number;
}

export const project = (p: Vec3, scale: number): Screen => ({
  x: (p.x - p.y) * COS30 * scale,
  y: ((p.x + p.y) * SIN30 - p.z) * scale,
});

/** Screen-space unit direction of each pipe axis. */
const axisScreen: Record<AxisId, Screen> = {
  "x+": { x: COS30, y: SIN30 },
  "x-": { x: -COS30, y: -SIN30 },
  "y+": { x: -COS30, y: SIN30 },
  "y-": { x: COS30, y: -SIN30 },
  "z+": { x: 0, y: -1 },
  "z-": { x: 0, y: 1 },
};

const axisLabel: Record<AxisId, string> = {
  "x+": "EAST",
  "x-": "WEST",
  "y+": "NORTH",
  "y-": "SOUTH",
  "z+": "UP",
  "z-": "DOWN",
};

const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scaleV = (v: Vec3, s: number): Vec3 => ({ x: v.x * s, y: v.y * s, z: v.z * s });
const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const lenV = (v: Vec3) => Math.hypot(v.x, v.y, v.z);
const unit = (v: Vec3): Vec3 => {
  const l = lenV(v) || 1;
  return scaleV(v, 1 / l);
};

/**
 * Parse a typed length into inches. Accepts shop notation:
 * `54`, `54"`, `4'`, `4'-6"`, `4' 6 1/2"`, `4ft 6in`, `4.5'`.
 * Returns null when nothing usable was typed.
 */
export function parseLengthInches(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, " ");
  if (!s) return null;
  let feet = 0;
  let inches = 0;
  let matched = false;

  const ft = s.match(/(-?\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot)/);
  if (ft) {
    feet = parseFloat(ft[1]);
    matched = true;
  }

  // whatever is left after the feet token holds the inches part
  const rest = ft ? s.slice((ft.index ?? 0) + ft[0].length) : s;
  const inPart = rest.replace(/["]|in(?:ch(?:es)?)?/g, " ").replace(/^[\s-]+/, "").trim();
  if (inPart) {
    const frac = inPart.match(/(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)/);
    if (frac) {
      inches = (frac[1] ? parseFloat(frac[1]) : 0) + parseFloat(frac[2]) / parseFloat(frac[3]);
      matched = true;
    } else {
      const num = inPart.match(/-?\d+(?:\.\d+)?/);
      if (num) {
        inches = parseFloat(num[0]);
        matched = true;
      }
    }
  }

  if (!matched) return null;
  const total = feet * 12 + inches;
  return Number.isFinite(total) ? total : null;
}

export type SketchMode = "draw" | "branch" | "place" | "support" | "term" | "note" | "pan";

interface Props {
  runs: PipeRun[];
  fittings: RunFitting[];
  cutList: CutPiece[];
  segTrims: Map<string, { in: number[]; out: number[] }>;
  placements: ComponentPlacement[];
  notes: SketchNote[];
  activeRunId: string | null;
  material: MaterialId;
  size: string;
  mode: SketchMode;
  componentKind: ComponentKind;
  onStart: (p: Vec3) => void;
  onBranch: (hostRunId: string, p: Vec3) => void;
  onExtend: (p: Vec3) => void;
  onFinish: () => void;
  onUndoVertex: () => void;
  onPlaceComponent: (runId: string, index: number, station: number) => void;
  onAddNote: (at: Vec3, text: string) => void;
  onRemoveComponent: (id: string) => void;
  onRemoveNote: (id: string) => void;
  /** placed supports resolved onto the geometry */
  supportPlacements?: SupportPlacement[];
  /** support type dropped in support mode */
  supportKind?: SupportKind;
  onPlaceSupport?: (runId: string, index: number, station: number) => void;
  onRemoveSupport?: (id: string) => void;
  /** placed terminations resolved onto the geometry */
  terminationPlacements?: TerminationPlacement[];
  /** termination type dropped in termination mode */
  terminationKind?: TerminationKind;
  onPlaceTermination?: (runId: string, end: "start" | "end") => void;
  onRemoveTermination?: (id: string) => void;
  /** elevation of the z = 0 plane, inches */
  baseElevation?: number;
  /** show EL marks and N/E coordinates */
  showElev?: boolean;
  /** cut-list piece tag currently highlighted */
  selectedTag?: string | null;
  /** show the P-xx mark numbers on the sheet */
  showTags?: boolean;
  /** select a cut-list piece by tapping it on the sheet */
  onSelectTag?: (tag: string | null) => void;
  /** dragged dimension line offsets, keyed `${runId}-${segIndex}` (signed inches) */
  dimOffsets?: Record<string, number>;
  /** user dragged a dimension line to a new offset */
  onDimOffset?: (key: string, offsetInches: number) => void;
  /** double-tap a dimension line to put it back on its automatic side */
  onDimResetOffset?: (key: string) => void;
  /** user typed a new length for a straight — re-lengths the spool */
  onSetSegmentLength?: (runId: string, index: number, inches: number) => void;
  /** when true, dimension lines can't be dragged or re-typed (prevents accidental edits) */
  dimsLocked?: boolean;
}

export default function IsoSketchCanvas({
  runs,
  fittings,
  cutList,
  segTrims,
  placements,
  notes,
  activeRunId,
  material,
  size,
  mode,
  componentKind,
  onStart,
  onBranch,
  onExtend,
  onFinish,
  onUndoVertex,
  onPlaceComponent,
  onAddNote,
  onRemoveComponent,
  onRemoveNote,
  supportPlacements = [],
  supportKind = "hanger",
  onPlaceSupport,
  onRemoveSupport,
  terminationPlacements = [],
  terminationKind = "cap",
  onPlaceTermination,
  onRemoveTermination,
  baseElevation = 0,
  showElev = false,
  selectedTag = null,
  showTags = true,
  onSelectTag,
  dimOffsets = {},
  onDimOffset,
  onDimResetOffset,
  onSetSegmentLength,
  dimsLocked = false,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [box, setBox] = useState({ w: 800, h: 520 });
  const [scale, setScale] = useState(3.2); // px per inch
  const [pan, setPan] = useState<Screen>({ x: 0, y: 0 });
  const [cursor, setCursor] = useState<Screen | null>(null);
  const panRef = useRef<{ start: Screen; pan: Screen } | null>(null);
  /** touch/pen input gets bigger hit areas and bigger markers */
  const [coarse, setCoarse] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches,
  );
  /** px radius for snapping onto vertices and runs */
  const hitR = coarse ? BRANCH_HIT * 2.4 : BRANCH_HIT;
  /** marker size multiplier */
  const mk = coarse ? 1.8 : 1;
  const [showHelp, setShowHelp] = useState(false);
  /** Auto-hide timer for the gesture overlay. */
  const helpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHelpTimer = () => {
    if (helpTimer.current) clearTimeout(helpTimer.current);
    helpTimer.current = null;
  };
  const openHelp = () => {
    clearHelpTimer();
    setShowHelp(true);
    helpTimer.current = setTimeout(() => setShowHelp(false), 8000);
  };
  const closeHelp = () => {
    clearHelpTimer();
    setShowHelp(false);
  };
  useEffect(() => clearHelpTimer, []);
  /** ISO joint symbology (solid butt-weld dots) */
  const [showWelds, setShowWelds] = useState(true);
  /** dimension lines with extension lines + arrows */
  const [showDims, setShowDims] = useState(true);
  /** dimension line being dragged to a new offset */
  const [dimDrag, setDimDrag] = useState<string | null>(null);
  /** dimension value being retyped */
  const [dimEdit, setDimEdit] = useState<{
    key: string;
    runId: string;
    index: number;
    at: Screen;
    value: string;
  } | null>(null);
  /** pending note being typed */
  const [pendingNote, setPendingNote] = useState<{ at: Vec3; screen: Screen; text: string } | null>(
    null,
  );

  const mat = getMaterial(material);

  /** Gesture cheat-sheet for the active mode, tailored to touch vs mouse. */
  const modeHelp = useMemo(() => {
    const zoom = coarse
      ? ["Pinch with two fingers to zoom", "Tap + / − / FIT for precise zoom and framing"]
      : ["Scroll wheel to zoom", "Click + / − / FIT for precise zoom and framing"];
    const panTips = coarse
      ? ["Drag one finger on empty space to pan", "Two-finger drag also pans"]
      : ["Shift-drag or middle-mouse drag to pan"];
    if (mode === "pan") {
      return {
        title: "Pan mode — view only",
        lines: [
          "Drag anywhere to move the sheet",
          "Nothing is drawn while Pan is active",
          ...zoom,
          "Switch to Draw or Branch to keep sketching",
        ],
      };
    }
    if (mode === "branch") {
      return {
        title: "Branch mode — tee off a run",
        lines: [
          coarse
            ? "Tap a diamond marker on a pipe to start a branch"
            : "Click a diamond marker on a pipe to start a branch",
          "Branches lock 90° to the host run",
          ...panTips,
          ...zoom,
        ],
      };
    }
    if (mode === "place") {
      return {
        title: "Place mode — valves & flanges",
        lines: [
          coarse ? "Tap a pipe to drop the selected item" : "Click a pipe to drop the selected item",
          "Position snaps to 3\" along the line",
          "Face-to-face is removed from the cut lengths",
          coarse ? "Tap an existing symbol to delete it" : "Click an existing symbol to delete it",
          ...zoom,
        ],
      };
    }
    if (mode === "note") {
      return {
        title: "Note mode — annotate the sheet",
        lines: [
          coarse ? "Tap anywhere to drop a note" : "Click anywhere to drop a note",
          "Type the text, then Enter to place it",
          "Notes print with the sketch",
          ...zoom,
        ],
      };
    }
    return {
      title: "Draw mode — run pipe",
      lines: [
        coarse
          ? "Tap to set the start point, tap again along an axis"
          : "Click to set the start point, click again along an axis",
        "Turns auto-insert the correct fitting",
        "Enter or Esc finishes the run · Backspace undoes a vertex",
        ...panTips,
        ...zoom,
      ],
    };
  }, [mode, coarse]);

  useEffect(() => {
    const el = svgRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setBox({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const origin = useMemo(
    () => ({ x: box.w / 2 + pan.x, y: box.h / 2 + pan.y }),
    [box, pan],
  );

  const toScreen = useCallback(
    (p: Vec3): Screen => {
      const s = project(p, scale);
      return { x: origin.x + s.x, y: origin.y + s.y };
    },
    [origin, scale],
  );

  const activeRun = runs.find((r) => r.id === activeRunId) ?? null;
  const lastPt = activeRun?.pts[activeRun.pts.length - 1] ?? null;

  /** Pointer -> point on the z = 0 plane, snapped to the grid. */
  const groundPoint = useCallback(
    (sx: number, sy: number): Vec3 => {
      const dx = (sx - origin.x) / scale;
      const dy = (sy - origin.y) / scale;
      const a = dx / COS30; // x - y
      const b = dy / SIN30; // x + y
      const x = Math.round(((a + b) / 2) / GRID) * GRID;
      const y = Math.round(((b - a) / 2) / GRID) * GRID;
      return { x, y, z: 0 };
    },
    [origin, scale],
  );

  /**
   * Branch/tee target. Existing interior vertices win over mid-segment points so a
   * branch always lands on real geometry; mid-segment hits snap to the 3" increment
   * and keep enough clearance from each end for the neighbouring fitting takeoffs.
   */
  const branchTarget = useMemo(() => {
    if (mode !== "branch" || !cursor || lastPt) return null;
    const branchSpec = getSizeSpec(material, size);
    let best: { runId: string; at: Vec3; d: number; snapped: "vertex" | "run" } | null = null;
    const consider = (c: {
      runId: string;
      at: Vec3;
      d: number;
      snapped: "vertex" | "run";
    }) => {
      // bias towards existing vertices
      const score = c.d - (c.snapped === "vertex" ? hitR * 0.6 : 0);
      const bestScore = best ? best.d - (best.snapped === "vertex" ? hitR * 0.6 : 0) : Infinity;
      if (score < bestScore) best = c;
    };

    for (const run of runs) {
      // 1. interior vertices (a tee can't sit on a free end)
      for (let i = 1; i < run.pts.length - 1; i++) {
        const sv = toScreen(run.pts[i]);
        const d = Math.hypot(cursor.x - sv.x, cursor.y - sv.y);
        if (d <= hitR) {
          consider({ runId: run.id, at: run.pts[i], d, snapped: "vertex" });
        }
      }

      // 2. points along each straight
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const sa = toScreen(a);
        const sb = toScreen(b);
        const vx = sb.x - sa.x;
        const vy = sb.y - sa.y;
        const l2 = vx * vx + vy * vy || 1;
        let t = ((cursor.x - sa.x) * vx + (cursor.y - sa.y) * vy) / l2;
        t = Math.max(0, Math.min(1, t));
        const px = sa.x + vx * t;
        const py = sa.y + vy * t;
        const d = Math.hypot(cursor.x - px, cursor.y - py);
        if (d > hitR) continue;

        const total = lenV(sub(b, a));
        const hostSpec = getSizeSpec(material, run.segSizes[i] ?? run.size);
        // keep the tee clear of whatever sits at each end of this straight
        const clear =
          Math.ceil((hostSpec.teeRun + Math.max(hostSpec.elbow90, branchSpec.teeBranch)) / SNAP) *
          SNAP;
        const lo = clear;
        const hi = total - clear;
        if (hi < lo) continue;
        const along = Math.min(hi, Math.max(lo, Math.round((t * total) / SNAP) * SNAP));
        const at = add(a, scaleV(unit(sub(b, a)), along));
        consider({ runId: run.id, at, d, snapped: "run" });
      }
    }
    return best as { runId: string; at: Vec3; d: number; snapped: "vertex" | "run" } | null;
  }, [mode, cursor, lastPt, runs, toScreen, material, size, hitR]);

  /**
   * Where a valve / flange set would land: nearest straight, station snapped to 3",
   * kept clear of the fitting take-outs at both ends and of anything already placed.
   */
  const placeTarget = useMemo(() => {
    if (mode !== "place" || !cursor) return null;
    const spec = getComponentSpec(material, componentKind, size);
    let best: {
      runId: string;
      index: number;
      station: number;
      d: number;
      center: Vec3;
      from: Vec3;
      to: Vec3;
      ok: boolean;
    } | null = null;

    for (const run of runs) {
      const t = segTrims.get(run.id);
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const sa = toScreen(a);
        const sb = toScreen(b);
        const vx = sb.x - sa.x;
        const vy = sb.y - sa.y;
        const l2 = vx * vx + vy * vy || 1;
        let u = ((cursor.x - sa.x) * vx + (cursor.y - sa.y) * vy) / l2;
        u = Math.max(0, Math.min(1, u));
        const d = Math.hypot(cursor.x - (sa.x + vx * u), cursor.y - (sa.y + vy * u));
        if (d > hitR) continue;

        const total = lenV(sub(b, a));
        const dir = unit(sub(b, a));
        const lo = (t?.out[i] ?? 0) + spec.length / 2;
        const hi = total - (t?.in[i + 1] ?? 0) - spec.length / 2;
        const raw = Math.round((u * total) / SNAP) * SNAP;
        const station = Math.min(Math.max(raw, lo), hi);
        const ok = hi >= lo;
        const center = add(a, scaleV(dir, station));
        const cand = {
          runId: run.id,
          index: i,
          station,
          d,
          center,
          from: add(center, scaleV(dir, -spec.length / 2)),
          to: add(center, scaleV(dir, spec.length / 2)),
          ok,
        };
        if (!best || d < best.d) best = cand;
      }
    }
    return best;
  }, [mode, cursor, runs, toScreen, hitR, material, componentKind, size, segTrims]);

  /** Existing component under the cursor (tap to delete in Place mode). */
  const hoverComponent = useMemo(() => {
    if (mode !== "place" || !cursor) return null;
    for (const p of placements) {
      const s = toScreen(p.center);
      if (Math.hypot(cursor.x - s.x, cursor.y - s.y) <= hitR * 0.7) return p;
    }
    return null;
  }, [mode, cursor, placements, toScreen, hitR]);

  /** Nearest point on a straight for dropping a hanger / guide / anchor. */
  const supportTarget = useMemo(() => {
    if (mode !== "support" || !cursor) return null;
    let best: {
      runId: string;
      index: number;
      station: number;
      d: number;
      at: Vec3;
      dir: Vec3;
      vertical: boolean;
    } | null = null;
    for (const run of runs) {
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const sa = toScreen(a);
        const sb = toScreen(b);
        const vx = sb.x - sa.x;
        const vy = sb.y - sa.y;
        const l2 = vx * vx + vy * vy || 1;
        let u = ((cursor.x - sa.x) * vx + (cursor.y - sa.y) * vy) / l2;
        u = Math.max(0, Math.min(1, u));
        const d = Math.hypot(cursor.x - (sa.x + vx * u), cursor.y - (sa.y + vy * u));
        if (d > hitR) continue;
        const total = lenV(sub(b, a));
        const dir = unit(sub(b, a));
        const station = Math.min(Math.max(Math.round((u * total) / SNAP) * SNAP, 0), total);
        const cand = {
          runId: run.id,
          index: i,
          station,
          d,
          at: add(a, scaleV(dir, station)),
          dir,
          vertical: Math.abs(dir.z) > 0.9,
        };
        if (!best || cand.d < best.d) best = cand;
      }
    }
    return best;
  }, [mode, cursor, runs, toScreen, hitR]);

  /** Tap an existing support to pull it back off the sheet. */
  const hoverSupport = useMemo(() => {
    if (mode !== "support" || !cursor) return null;
    for (const s of supportPlacements) {
      const p = toScreen(s.at);
      if (Math.hypot(cursor.x - p.x, cursor.y - p.y) <= hitR * 0.7) return s;
    }
    return null;
  }, [mode, cursor, supportPlacements, toScreen, hitR]);

  /** Free ends of every run — where a cap, flange or nozzle can land. */
  const freeEnds = useMemo(() => {
    if (mode !== "term") return [];
    const out: { runId: string; end: "start" | "end"; at: Vec3; dir: Vec3; size: string }[] = [];
    for (const run of runs) {
      if (run.pts.length < 2) continue;
      const last = run.pts.length - 1;
      if (!run.branch) {
        out.push({
          runId: run.id,
          end: "start",
          at: run.pts[0],
          dir: unit(sub(run.pts[0], run.pts[1])),
          size: run.segSizes[0] ?? run.size,
        });
      }
      out.push({
        runId: run.id,
        end: "end",
        at: run.pts[last],
        dir: unit(sub(run.pts[last], run.pts[last - 1])),
        size: run.segSizes[last - 1] ?? run.size,
      });
    }
    return out;
  }, [mode, runs]);

  const termTarget = useMemo(() => {
    if (mode !== "term" || !cursor) return null;
    let best: (typeof freeEnds)[number] | null = null;
    let bd = hitR * 1.4;
    for (const e of freeEnds) {
      const s = toScreen(e.at);
      const d = Math.hypot(cursor.x - s.x, cursor.y - s.y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    return best;
  }, [mode, cursor, freeEnds, toScreen, hitR]);


  /**
   * Axes the next click may use. A branch's first leg is locked square to its
   * host run so the derived tee is always a true 90° take-off.
   */
  const allowedAxes = useMemo<AxisId[]>(() => {
    const all = Object.keys(axisScreen) as AxisId[];
    if (!activeRun || activeRun.pts.length !== 1) return all;
    const hd = activeRun.branch?.hostDir;
    if (!hd) return all;
    return all.filter((a) => {
      const v = AXES[a];
      return Math.abs(v.x * hd.x + v.y * hd.y + v.z * hd.z) < 0.01;
    });
  }, [activeRun]);

  /** Preview point constrained to the closest pipe axis. */
  const preview = useMemo(() => {
    if (!lastPt || !cursor) return null;
    const from = toScreen(lastPt);
    const dx = cursor.x - from.x;
    const dy = cursor.y - from.y;
    let best: { axis: AxisId; inches: number } | null = null;
    allowedAxes.forEach((axis) => {
      const d = axisScreen[axis];
      const proj = (dx * d.x + dy * d.y) / scale;
      if (proj <= 0) return;
      if (!best || proj > best.inches) best = { axis, inches: proj };
    });
    if (!best) return null;
    const inches = Math.max(SNAP, Math.round(best.inches / SNAP) * SNAP);
    const to = add(lastPt, scaleV(AXES[best.axis], inches));
    return { axis: best.axis, inches, to };
  }, [lastPt, cursor, toScreen, scale, allowedAxes]);

  /** Dashed rays along every axis the next click may lock to, with 12" ticks. */
  const axisGuides = useMemo(() => {
    if (!lastPt) return [];
    const from = toScreen(lastPt);
    const reach = Math.max(box.w, box.h);
    return allowedAxes.map((axis) => {
      const d = axisScreen[axis];
      const ticks: Screen[] = [];
      const stepPx = GRID * scale;
      if (stepPx > 8) {
        for (let px = stepPx; px < reach; px += stepPx) {
          ticks.push({ x: from.x + d.x * px, y: from.y + d.y * px });
        }
      }
      return {
        axis,
        from,
        to: { x: from.x + d.x * reach, y: from.y + d.y * reach },
        ticks,
        active: preview?.axis === axis,
      };
    });
  }, [lastPt, toScreen, allowedAxes, box, scale, preview]);

  /** Turn marker at the working end: arc + the fitting angle the next click makes. */
  const turnMarker = useMemo(() => {
    if (!activeRun || !lastPt || !preview || activeRun.pts.length < 2) return null;
    const prev = activeRun.pts[activeRun.pts.length - 2];
    const inDir = unit(sub(lastPt, prev));
    const outDir = AXES[preview.axis];
    const dot = inDir.x * outDir.x + inDir.y * outDir.y + inDir.z * outDir.z;
    const angle = Math.round((Math.acos(Math.max(-1, Math.min(1, dot))) * 180) / Math.PI);
    if (angle === 0) return { angle: 0, label: "STRAIGHT", d: "", at: toScreen(lastPt) };
    const c = toScreen(lastPt);
    const r = 16;
    const a = toScreen(sub(lastPt, scaleV(inDir, r / scale)));
    const b = toScreen(add(lastPt, scaleV(outDir, r / scale)));
    const ax = { x: c.x - (c.x - a.x), y: c.y - (c.y - a.y) };
    return {
      angle,
      label: `${angle}° TURN`,
      d: `M ${ax.x} ${ax.y} Q ${c.x} ${c.y} ${b.x} ${b.y}`,
      at: c,
    };
  }, [activeRun, lastPt, preview, toScreen, scale]);

  /** Every vertex a branch can legally lock onto (branch mode, before drawing). */
  const branchVertices = useMemo(() => {
    if (mode !== "branch" || lastPt) return [];
    const out: Screen[] = [];
    for (const run of runs) {
      for (let i = 1; i < run.pts.length - 1; i++) out.push(toScreen(run.pts[i]));
    }
    return out;
  }, [mode, lastPt, runs, toScreen]);

  /** latest derived values, readable from pointer handlers without stale closures */
  const previewRef = useRef(preview);
  previewRef.current = preview;
  const branchTargetRef = useRef(branchTarget);
  branchTargetRef.current = branchTarget;
  const placeTargetRef = useRef(placeTarget);
  placeTargetRef.current = placeTarget;
  const hoverComponentRef = useRef(hoverComponent);
  hoverComponentRef.current = hoverComponent;
  const supportTargetRef = useRef(supportTarget);
  supportTargetRef.current = supportTarget;
  const hoverSupportRef = useRef(hoverSupport);
  hoverSupportRef.current = hoverSupport;
  const termTargetRef = useRef(termTarget);
  termTargetRef.current = termTarget;
  const lastPtRef = useRef(lastPt);
  lastPtRef.current = lastPt;

  /** Commit a tap/click at a canvas position. */
  const commitAt = useCallback(
    (sx: number, sy: number) => {
      if (mode === "pan") return;
      if (mode === "note") {
        const at = groundPoint(sx, sy);
        setPendingNote({ at, screen: { x: sx, y: sy }, text: "" });
        return;
      }
      if (mode === "place") {
        const hov = hoverComponentRef.current;
        if (hov) {
          onRemoveComponent(hov.id);
          return;
        }
        const pt = placeTargetRef.current;
        if (pt?.ok) onPlaceComponent(pt.runId, pt.index, pt.station);
        return;
      }
      if (mode === "support") {
        const hov = hoverSupportRef.current;
        if (hov) {
          onRemoveSupport?.(hov.id);
          return;
        }
        const st = supportTargetRef.current;
        if (st) onPlaceSupport?.(st.runId, st.index, st.station);
        return;
      }
      if (mode === "term") {
        const tt = termTargetRef.current;
        if (!tt) return;
        const existing = terminationPlacements.find(
          (t) => t.runId === tt.runId && t.end === tt.end,
        );
        if (existing && existing.kind === terminationKind) onRemoveTermination?.(existing.id);
        else onPlaceTermination?.(tt.runId, tt.end);
        return;
      }
      if (!lastPtRef.current) {
        if (mode === "branch") {
          const bt = branchTargetRef.current;
          if (bt) onBranch(bt.runId, bt.at);
          return;
        }
        onStart(groundPoint(sx, sy));
      } else if (previewRef.current) {
        onExtend(previewRef.current.to);
      }
    },
    [
      groundPoint,
      onStart,
      onExtend,
      mode,
      onBranch,
      onPlaceComponent,
      onRemoveComponent,
      onPlaceSupport,
      onRemoveSupport,
      onPlaceTermination,
      onRemoveTermination,
      terminationPlacements,
      terminationKind,
    ],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (pendingNote) return;
      if (e.key === "Escape" || e.key === "Enter") onFinish();
      if (e.key === "Backspace" && activeRunId) {
        e.preventDefault();
        onUndoVertex();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFinish, onUndoVertex, activeRunId, pendingNote]);

  const onWheel = (e: React.WheelEvent) => {
    setScale((s) => Math.min(14, Math.max(0.6, s * (e.deltaY > 0 ? 0.9 : 1.1))));
  };

  const zoomBy = (f: number) =>
    setScale((s) => Math.min(14, Math.max(0.6, s * f)));

  /** Frame all drawn geometry, or reset the view when the sheet is empty. */
  const fitView = useCallback(() => {
    const pts = runs.flatMap((r) => r.pts);
    if (!pts.length) {
      setScale(3.2);
      setPan({ x: 0, y: 0 });
      return;
    }
    const pj = pts.map((p) => project(p, 1));
    const minX = Math.min(...pj.map((p) => p.x));
    const maxX = Math.max(...pj.map((p) => p.x));
    const minY = Math.min(...pj.map((p) => p.y));
    const maxY = Math.max(...pj.map((p) => p.y));
    const w = Math.max(maxX - minX, 1);
    const h = Math.max(maxY - minY, 1);
    const s = Math.min(14, Math.max(0.6, Math.min((box.w - 90) / w, (box.h - 90) / h)));
    setScale(s);
    setPan({ x: -((minX + maxX) / 2) * s, y: -((minY + maxY) / 2) * s });
  }, [runs, box]);


  const pointers = useRef(new Map<number, Screen>());
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  const tapRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const localPos = (e: { clientX: number; clientY: number }): Screen => {
    const rect = svgRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const twoFingerDist = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // any canvas gesture dismisses the cheat sheet
    if (showHelp) closeHelp();
    if (e.pointerType !== "mouse") setCoarse(true);
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // touch has no hover: place the cursor on touch-down so the preview exists
    if (mode !== "pan") setCursor(localPos(e));

    if (pointers.current.size === 2) {
      pinchRef.current = { dist: twoFingerDist(), scale };
      tapRef.current = null;
      panRef.current = { start: { x: e.clientX, y: e.clientY }, pan };
      return;
    }
    if (mode === "pan" || e.button === 1 || e.shiftKey) {
      panRef.current = { start: { x: e.clientX, y: e.clientY }, pan };
      tapRef.current = null;
      return;
    }
    tapRef.current = { x: e.clientX, y: e.clientY, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId)) {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (pointers.current.size === 2 && pinchRef.current) {
      const d = twoFingerDist();
      const ratio = d / (pinchRef.current.dist || 1);
      setScale(Math.min(14, Math.max(0.6, pinchRef.current.scale * ratio)));
      return;
    }

    if (mode !== "pan") setCursor(localPos(e));

    const tap = tapRef.current;
    if (tap && !tap.moved) {
      const slop = e.pointerType === "mouse" ? 6 : 14;
      const moved = Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > slop;
      if (moved) {
        tap.moved = true;
        // a finger drag pans the view; a tap draws
        if (e.pointerType !== "mouse" && !panRef.current) {
          panRef.current = { start: { x: tap.x, y: tap.y }, pan };
        }
      }
    }

    if (panRef.current) {
      setPan({
        x: panRef.current.pan.x + (e.clientX - panRef.current.start.x),
        y: panRef.current.pan.y + (e.clientY - panRef.current.start.y),
      });
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;

    const tap = tapRef.current;
    tapRef.current = null;
    const wasPanning = !!panRef.current;
    panRef.current = null;

    if (tap && !tap.moved && !wasPanning) {
      const p = localPos(e);
      commitAt(p.x, p.y);
      if (e.pointerType !== "mouse") setCursor(p);
    }
  };

  /* ---------------- dimension line dragging ---------------- */

  interface DimHandle {
    key: string;
    n: Screen;
    base: Screen;
  }

  const startDimDrag = (e: React.PointerEvent, d: DimHandle) => {
    if (!onDimOffset || dimsLocked) return;
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    setDimDrag(d.key);
  };

  const moveDimDrag = (e: React.PointerEvent, d: DimHandle) => {
    if (dimDrag !== d.key || !onDimOffset || dimsLocked) return;
    e.stopPropagation();
    const p = localPos(e);
    const offsetPx = (p.x - d.base.x) * d.n.x + (p.y - d.base.y) * d.n.y;
    onDimOffset(d.key, offsetPx / scale);
  };

  const endDimDrag = (e: React.PointerEvent) => {
    if (!dimDrag) return;
    e.stopPropagation();
    setDimDrag(null);
  };


  /** Background isometric grid on the ground plane. */
  const gridLines = useMemo(() => {
    const n = 14;
    const lines: { a: Screen; b: Screen }[] = [];
    for (let i = -n; i <= n; i++) {
      lines.push({
        a: toScreen({ x: i * GRID, y: -n * GRID, z: 0 }),
        b: toScreen({ x: i * GRID, y: n * GRID, z: 0 }),
      });
      lines.push({
        a: toScreen({ x: -n * GRID, y: i * GRID, z: 0 }),
        b: toScreen({ x: n * GRID, y: i * GRID, z: 0 }),
      });
    }
    return lines;
  }, [toScreen]);

  /**
   * Single-line ISO geometry: every cut piece is a straight, and each elbow gets
   * an arc through its vertex. Valves and flanges leave a real gap in the line.
   */
  const runStraights = useMemo(
    () =>
      cutList.map((p) => {
        const a = toScreen(p.from);
        const b = toScreen(p.to);
        return {
          key: `${p.runId}-${p.index}-${p.sub}`,
          tag: p.tag,
          a,
          b,
          mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          screenLen: Math.hypot(b.x - a.x, b.y - a.y),
        };
      }),
    [cutList, toScreen],
  );

  const elbowArcs = useMemo(() => {
    const out: { key: string; d: string }[] = [];
    for (const f of fittings) {
      if (!f.kind.startsWith("elbow")) continue;
      const run = runs.find((r) => r.id === f.runId);
      const t = segTrims.get(f.runId);
      if (!run || !t) continue;
      const i = f.index;
      const prev = run.pts[i - 1];
      const next = run.pts[i + 1];
      if (!prev || !next) continue;
      const pdir = unit(sub(run.pts[i], prev));
      const ndir = unit(sub(next, run.pts[i]));
      const a = toScreen(sub(run.pts[i], scaleV(pdir, t.in[i] ?? 0)));
      const b = toScreen(add(run.pts[i], scaleV(ndir, t.out[i] ?? 0)));
      const c = toScreen(run.pts[i]);
      out.push({ key: `${f.runId}-${i}`, d: `M ${a.x} ${a.y} Q ${c.x} ${c.y} ${b.x} ${b.y}` });
    }
    return out;
  }, [fittings, runs, segTrims, toScreen]);

  /**
   * Joint locations, per single-line ISO convention: a joint sits wherever a
   * straight piece meets a fitting take-off. Drawn as a solid dot — the standard
   * shop butt-weld symbol.
   */
  const weldPoints = useMemo(() => {
    if (!showWelds) return [] as Screen[];
    const seen = new Set<string>();
    const out: Screen[] = [];
    const push = (p: Vec3) => {
      const s = toScreen(p);
      const k = `${Math.round(s.x)}|${Math.round(s.y)}`;
      if (seen.has(k)) return;
      seen.add(k);
      out.push(s);
    };
    for (const piece of cutList) {
      if (piece.trimStart > 0) push(piece.from);
      if (piece.trimEnd > 0) push(piece.to);
    }
    return out;
  }, [cutList, toScreen, showWelds]);

  /**
   * Offset dimension lines with extension lines and tick arrows.
   * The offset is automatic unless the user has dragged the line, in which case
   * `dimOffsets` holds a signed distance (inches) along the line normal.
   */
  const dimensions = useMemo(() => {
    if (!showDims) return [];
    const out: {
      key: string;
      runId: string;
      index: number;
      a: Screen;
      b: Screen;
      ea: Screen;
      eb: Screen;
      mid: Screen;
      /** screen-space unit normal the offset is measured along */
      n: Screen;
      /** midpoint of the pipe itself, the offset origin */
      base: Screen;
      inches: number;
      text: string;
      angle: number;
      custom: boolean;
    }[] = [];
    const off = 16 * mk;
    for (const run of runs) {
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const sa = toScreen(a);
        const sb = toScreen(b);
        const dx = sb.x - sa.x;
        const dy = sb.y - sa.y;
        const l = Math.hypot(dx, dy) || 1;
        if (l < 26) continue;
        // offset to the side that points away from the sheet centre
        const nx = -dy / l;
        const ny = dx / l;
        const midX = (sa.x + sb.x) / 2;
        const midY = (sa.y + sb.y) / 2;
        const key = `${run.id}-${i}`;
        const override = dimOffsets[key];
        const custom = typeof override === "number";
        const away = (midX - origin.x) * nx + (midY - origin.y) * ny >= 0 ? 1 : -1;
        const dist = custom ? override * scale : off * away;
        const ox = nx * dist;
        const oy = ny * dist;
        const da = { x: sa.x + ox, y: sa.y + oy };
        const db = { x: sb.x + ox, y: sb.y + oy };
        let angle = (Math.atan2(db.y - da.y, db.x - da.x) * 180) / Math.PI;
        if (angle > 90 || angle < -90) angle += 180;
        const inches = lenV(sub(b, a));
        out.push({
          key,
          runId: run.id,
          index: i,
          a: da,
          b: db,
          ea: sa,
          eb: sb,
          mid: { x: (da.x + db.x) / 2, y: (da.y + db.y) / 2 },
          n: { x: nx, y: ny },
          base: { x: midX, y: midY },
          inches,
          text: formatFtIn(inches),
          angle,
          custom,
        });
      }
    }
    return out;
  }, [runs, toScreen, showDims, mk, origin, dimOffsets, scale]);


  /** North arrow direction, drawn along the +y isometric axis. */
  const northDir = useMemo(() => {
    const a = project({ x: 0, y: 0, z: 0 }, 1);
    const b = project({ x: 0, y: -1, z: 0 }, 1);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const l = Math.hypot(dx, dy) || 1;
    return { x: dx / l, y: dy / l };
  }, []);

  const previewSpec = getSizeSpec(material, size);
  const fontFamily = "ui-monospace, monospace";

  return (
    <div className="relative h-full w-full select-none">
      <svg
        ref={svgRef}
        width={box.w}
        height={box.h}
        className="h-full w-full touch-none"
        style={{
          cursor:
            mode === "pan"
              ? "grab"
              : mode === "note"
                ? "text"
                : activeRunId
                  ? "crosshair"
                  : mode === "branch" || mode === "place"
                    ? "copy"
                    : "cell",
        }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setCursor(null);
        }}
      >
        {/* ground grid */}
        <g stroke="hsl(var(--border))" strokeWidth={0.5} opacity={0.5}>
          {gridLines.map((l, i) => (
            <line key={i} x1={l.a.x} y1={l.a.y} x2={l.b.x} y2={l.b.y} />
          ))}
        </g>

        {/* dimension lines — drag to reposition, tap the value to re-length */}
        {dimensions.map((d) => {
          const active = dimDrag === d.key;
          const col = active || d.custom ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))";
          const fs = 10 * (coarse ? 1.3 : 1);
          const lockR = 4.2 * mk;
          const lockAt = { x: d.mid.x, y: d.mid.y + 9 };
          return (
            <g key={`dim-${d.key}`} stroke={col} strokeWidth={0.8} opacity={active ? 1 : 0.85}>
              <line x1={d.ea.x} y1={d.ea.y} x2={d.a.x} y2={d.a.y} strokeDasharray="2 3" />
              <line x1={d.eb.x} y1={d.eb.y} x2={d.b.x} y2={d.b.y} strokeDasharray="2 3" />
              {/* frozen dimension: highlighted band behind the line */}
              {dimsLocked && (
                <line
                  x1={d.a.x}
                  y1={d.a.y}
                  x2={d.b.x}
                  y2={d.b.y}
                  stroke={col}
                  strokeWidth={6 * mk}
                  strokeLinecap="round"
                  opacity={0.14}
                />
              )}
              <line
                x1={d.a.x}
                y1={d.a.y}
                x2={d.b.x}
                y2={d.b.y}
                strokeDasharray={dimsLocked ? "5 3" : undefined}
              />
              <circle cx={d.a.x} cy={d.a.y} r={1.6 * mk} fill={col} stroke="none" />
              <circle cx={d.b.x} cy={d.b.y} r={1.6 * mk} fill={col} stroke="none" />
              <text
                x={d.mid.x}
                y={d.mid.y - 4}
                textAnchor="middle"
                transform={`rotate(${d.angle} ${d.mid.x} ${d.mid.y})`}
                fill={col}
                stroke="none"
                style={{ fontSize: fs, fontFamily }}
              >
                {d.text}
              </text>

              {/* padlock badge marking the line as frozen */}
              {dimsLocked && (
                <g transform={`rotate(${d.angle} ${d.mid.x} ${d.mid.y})`}>
                  <title>Dimension locked — unlock Dims to move or edit</title>
                  <circle
                    cx={lockAt.x}
                    cy={lockAt.y}
                    r={lockR}
                    fill="hsl(var(--background))"
                    stroke={col}
                    strokeWidth={0.8}
                  />
                  <rect
                    x={lockAt.x - lockR * 0.45}
                    y={lockAt.y - lockR * 0.1}
                    width={lockR * 0.9}
                    height={lockR * 0.75}
                    rx={lockR * 0.15}
                    fill={col}
                    stroke="none"
                  />
                  <path
                    d={`M ${lockAt.x - lockR * 0.28} ${lockAt.y - lockR * 0.1}
                        v ${-lockR * 0.3}
                        a ${lockR * 0.28} ${lockR * 0.28} 0 0 1 ${lockR * 0.56} 0
                        v ${lockR * 0.3}`}
                    fill="none"
                    stroke={col}
                    strokeWidth={0.9}
                  />
                </g>
              )}


              {/* drag handle: the whole dimension line (disabled while locked) */}
              {!dimsLocked && (
              <line
                x1={d.a.x}
                y1={d.a.y}
                x2={d.b.x}
                y2={d.b.y}
                stroke="transparent"
                strokeWidth={18 * (coarse ? 1.6 : 1)}
                style={{ cursor: "move", pointerEvents: "stroke" }}
                onPointerDown={(e) => startDimDrag(e, d)}
                onPointerMove={(e) => moveDimDrag(e, d)}
                onPointerUp={endDimDrag}
                onPointerCancel={endDimDrag}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  onDimResetOffset?.(d.key);
                }}
              />
              )}

              {/* tap target on the value itself */}
              {!dimsLocked && (
              <g transform={`rotate(${d.angle} ${d.mid.x} ${d.mid.y})`}>
                <rect
                  x={d.mid.x - fs * 2.6}
                  y={d.mid.y - fs - 6}
                  width={fs * 5.2}
                  height={fs + 8}
                  fill="transparent"
                  stroke="none"
                  style={{ cursor: "text", pointerEvents: "all" }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDimDrag(null);
                    setDimEdit({
                      key: d.key,
                      runId: d.runId,
                      index: d.index,
                      at: d.mid,
                      value: d.text,
                    });
                  }}
                />
              </g>
              )}
            </g>
          );
        })}


        {/* highlight for the piece selected in the cut list */}
        {runStraights
          .filter((s) => s.tag === selectedTag)
          .map((s) => (
            <line
              key={`hl-${s.key}`}
              x1={s.a.x}
              y1={s.a.y}
              x2={s.b.x}
              y2={s.b.y}
              stroke="hsl(var(--primary))"
              strokeWidth={9 * (coarse ? 1.3 : 1)}
              strokeLinecap="round"
              opacity={0.3}
            />
          ))}

        {/* pipe straights */}
        <g stroke="hsl(var(--foreground))" strokeWidth={2} strokeLinecap="round" fill="none">
          {runStraights.map((s) => (
            <line
              key={s.key}
              x1={s.a.x}
              y1={s.a.y}
              x2={s.b.x}
              y2={s.b.y}
              stroke={s.tag === selectedTag ? "hsl(var(--primary))" : undefined}
              strokeWidth={s.tag === selectedTag ? 3 : undefined}
            />
          ))}
          {elbowArcs.map((a) => (
            <path key={a.key} d={a.d} />
          ))}
        </g>

        {/* tap targets: select a piece by tapping the pipe itself (pan mode) */}
        {onSelectTag &&
          mode === "pan" &&
          runStraights.map((s) => (
            <line
              key={`pick-${s.key}`}
              x1={s.a.x}
              y1={s.a.y}
              x2={s.b.x}
              y2={s.b.y}
              stroke="transparent"
              strokeWidth={16 * (coarse ? 1.6 : 1)}
              strokeLinecap="round"
              style={{ cursor: "pointer" }}
              onPointerDown={(e) => {
                e.stopPropagation();
                onSelectTag(s.tag === selectedTag ? null : s.tag);
              }}
            />
          ))}

        {/* cut-list mark numbers */}
        {showTags &&
          runStraights
            .filter((s) => s.screenLen > 22 || s.tag === selectedTag)
            .map((s) => {
              const w = s.tag.length * 5.6 * (coarse ? 1.25 : 1) + 6;
              const h = 12 * (coarse ? 1.25 : 1);
              const on = s.tag === selectedTag;
              return (
                <g
                  key={`mk-${s.key}`}
                  style={onSelectTag ? { cursor: "pointer" } : undefined}
                  onPointerDown={
                    onSelectTag
                      ? (e) => {
                          e.stopPropagation();
                          onSelectTag(on ? null : s.tag);
                        }
                      : undefined
                  }
                >
                  <rect
                    x={s.mid.x - w / 2}
                    y={s.mid.y - h - 4}
                    width={w}
                    height={h}
                    rx={3}
                    fill="hsl(var(--background))"
                    stroke={on ? "hsl(var(--primary))" : "hsl(var(--border))"}
                    strokeWidth={on ? 1.4 : 0.8}
                    opacity={0.95}
                  />
                  <text
                    x={s.mid.x}
                    y={s.mid.y - 4 - h / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className={on ? "fill-primary" : "fill-muted-foreground"}
                    style={{ fontSize: 9 * (coarse ? 1.25 : 1), fontFamily }}
                  >
                    {s.tag}
                  </text>
                </g>
              );
            })}

        {/* run size tags */}
        {runs.map((run) =>
          run.pts[0] ? (
            <text
              key={`tag-${run.id}`}
              x={toScreen(run.pts[0]).x + 8}
              y={toScreen(run.pts[0]).y + 14}
              className="fill-primary"
              style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily }}
            >
              {run.segSizes[0] ?? run.size} {getMaterial(run.material).sheetTag}
            </text>
          ) : null,
        )}

        {/* fitting symbols */}
        {fittings.map((f, i) => {
          const s = toScreen(f.at);
          if (f.kind === "tee") {
            const spec = getSizeSpec(material, f.size);
            const tip = f.branchDir
              ? toScreen(add(f.at, scaleV(f.branchDir, spec.teeBranch)))
              : s;
            return (
              <g key={i}>
                <line
                  x1={s.x}
                  y1={s.y}
                  x2={tip.x}
                  y2={tip.y}
                  stroke="hsl(var(--foreground))"
                  strokeWidth={2}
                />
                <text
                  x={s.x + 7}
                  y={s.y - 7}
                  className="fill-primary"
                  style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily }}
                >
                  {f.size} TEE
                </text>
              </g>
            );
          }
          if (f.kind === "reducer") {
            return (
              <g key={i}>
                <path
                  d={`M ${s.x - 5} ${s.y - 5} L ${s.x + 5} ${s.y - 2} L ${s.x + 5} ${
                    s.y + 2
                  } L ${s.x - 5} ${s.y + 5} Z`}
                  fill="hsl(var(--background))"
                  stroke="hsl(var(--primary))"
                  strokeWidth={1.4}
                />
                <text
                  x={s.x + 8}
                  y={s.y - 6}
                  className="fill-primary"
                  style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily }}
                >
                  {f.size}×{f.sizeOut} RED
                </text>
              </g>
            );
          }
          return (
            <g key={i}>
              <text
                x={s.x + 7}
                y={s.y - 7}
                className="fill-primary"
                style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily }}
              >
                {f.angle}° ELL
              </text>
            </g>
          );
        })}

        {/* placed valves, flanges and specialties */}
        {placements.map((p) => (
          <ComponentSymbol
            key={p.id}
            kind={p.kind}
            a={toScreen(p.from)}
            b={toScreen(p.to)}
            scale={mk}
            label={p.tag ?? `${p.size} ${p.short}`}
          />
        ))}

        {/* supports & hangers */}
        {supportPlacements.map((s) => {
          const at = toScreen(s.at);
          const along = toScreen(add(s.at, s.dir));
          return (
            <SupportSymbol
              key={s.id}
              kind={s.kind}
              at={at}
              along={{ x: along.x - at.x, y: along.y - at.y }}
              vertical={s.vertical}
              scale={mk}
              mark={s.mark}
            />
          );
        })}

        {/* support ghost under the cursor */}
        {supportTarget && !hoverSupport && (
          (() => {
            const at = toScreen(supportTarget.at);
            const along = toScreen(add(supportTarget.at, supportTarget.dir));
            return (
              <SupportSymbol
                kind={supportKind}
                at={at}
                along={{ x: along.x - at.x, y: along.y - at.y }}
                vertical={supportTarget.vertical}
                scale={mk}
                ghost
                mark={getSupportSpec(material, supportKind, size).short}
              />
            );
          })()
        )}

        {hoverSupport && (
          <circle
            cx={toScreen(hoverSupport.at).x}
            cy={toScreen(hoverSupport.at).y}
            r={9 * mk}
            fill="none"
            stroke="hsl(var(--destructive))"
            strokeWidth={1.6}
            strokeDasharray="3 3"
          />
        )}

        {/* terminations & equipment nozzles */}
        {terminationPlacements.map((t) => {
          const at = toScreen(t.at);
          const out = toScreen(add(t.at, t.dir));
          return (
            <TerminationSymbol
              key={t.id}
              kind={t.kind}
              at={at}
              dir={{ x: out.x - at.x, y: out.y - at.y }}
              scale={mk}
              mark={t.tag ?? t.mark}
            />
          );
        })}

        {/* free-end targets + termination ghost */}
        {mode === "term" &&
          freeEnds.map((e) => {
            const s = toScreen(e.at);
            const active = termTarget?.runId === e.runId && termTarget?.end === e.end;
            return (
              <circle
                key={`${e.runId}-${e.end}`}
                cx={s.x}
                cy={s.y}
                r={(active ? 8 : 5) * mk}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={active ? 2 : 1.2}
                opacity={active ? 1 : 0.5}
              />
            );
          })}
        {termTarget && (
          (() => {
            const at = toScreen(termTarget.at);
            const out = toScreen(add(termTarget.at, termTarget.dir));
            return (
              <TerminationSymbol
                kind={terminationKind}
                at={at}
                dir={{ x: out.x - at.x, y: out.y - at.y }}
                scale={mk}
                ghost
                mark={getTerminationSpec(material, terminationKind, termTarget.size).short}
              />
            );
          })()
        )}

        {/* elevation marks on risers + N/E coordinates at run origins */}
        {showElev &&
          runs.map((run) => {
            const marks: JSX.Element[] = [];
            for (let i = 0; i < run.pts.length; i++) {
              const p = run.pts[i];
              const prev = i > 0 ? run.pts[i - 1] : null;
              const next = i < run.pts.length - 1 ? run.pts[i + 1] : null;
              const riser =
                (prev && Math.abs(p.z - prev.z) > 0.01) || (next && Math.abs(next.z - p.z) > 0.01);
              if (!riser && i !== 0 && i !== run.pts.length - 1) continue;
              const s = toScreen(p);
              marks.push(
                <text
                  key={`el-${run.id}-${i}`}
                  x={s.x + 9}
                  y={s.y + 13}
                  className="fill-muted-foreground"
                  style={{ fontSize: 9 * (coarse ? 1.25 : 1), fontFamily }}
                >
                  EL {formatElevation(baseElevation + p.z)}
                </text>,
              );
            }
            const o = toScreen(run.pts[0]);
            marks.push(
              <text
                key={`ne-${run.id}`}
                x={o.x + 9}
                y={o.y + 24}
                className="fill-muted-foreground"
                style={{ fontSize: 8.5 * (coarse ? 1.25 : 1), fontFamily }}
              >
                {formatCoords(run.pts[0].x, run.pts[0].y)}
              </text>,
            );
            return <g key={`elg-${run.id}`}>{marks}</g>;
          })}


        {/* joint symbols (solid dots, ISO single-line convention) */}
        {weldPoints.map((w, i) => (
          <circle
            key={`w${i}`}
            cx={w.x}
            cy={w.y}
            r={2.6 * mk}
            fill="hsl(var(--foreground))"
            stroke="hsl(var(--background))"
            strokeWidth={0.8}
          />
        ))}

        {/* notes */}
        {notes.map((n) => {
          const s = toScreen(n.at);
          return (
            <g key={n.id} onClick={() => onRemoveNote(n.id)} style={{ cursor: "pointer" }}>
              <line
                x1={s.x}
                y1={s.y}
                x2={s.x + 14}
                y2={s.y - 14}
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={0.8}
              />
              <circle cx={s.x} cy={s.y} r={2 * mk} fill="hsl(var(--muted-foreground))" />
              <text
                x={s.x + 16}
                y={s.y - 14}
                className="fill-foreground"
                style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily }}
              >
                {n.text}
              </text>
            </g>
          );
        })}

        {/* endpoints */}
        {runs.map((run) => {
          const ends = run.branch
            ? [run.pts[run.pts.length - 1]]
            : [run.pts[0], run.pts[run.pts.length - 1]];
          return ends.map((p, i) => {
            const s = toScreen(p);
            return (
              <rect
                key={`${run.id}-${i}`}
                x={s.x - 3 * mk}
                y={s.y - 3 * mk}
                width={6 * mk}
                height={6 * mk}
                fill="hsl(var(--background))"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.2 * mk}
              />
            );
          });
        })}

        {/* branch target indicator */}
        {branchTarget && (
          <g>
            <circle
              cx={toScreen(branchTarget.at).x}
              cy={toScreen(branchTarget.at).y}
              r={6 * mk}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={2 * mk}
            />
            {branchTarget.snapped === "vertex" && (
              <circle
                cx={toScreen(branchTarget.at).x}
                cy={toScreen(branchTarget.at).y}
                r={10 * mk}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth={1 * mk}
                strokeDasharray="3 3"
              />
            )}
            <text
              x={toScreen(branchTarget.at).x + 12}
              y={toScreen(branchTarget.at).y - 9}
              className="fill-primary"
              style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily }}
            >
              {branchTarget.snapped === "vertex" ? "TEE @ VERTEX" : "TEE HERE"}
            </text>
          </g>
        )}

        {/* component placement ghost */}
        {mode === "place" && !hoverComponent && placeTarget && (
          <g opacity={placeTarget.ok ? 1 : 0.4}>
            <ComponentSymbol
              kind={componentKind}
              a={toScreen(placeTarget.from)}
              b={toScreen(placeTarget.to)}
              scale={mk}
              ghost
              label={placeTarget.ok ? `${size} ${getComponentSpec(material, componentKind, size).short}` : "NO ROOM"}
            />
          </g>
        )}
        {mode === "place" && hoverComponent && (
          <g>
            <circle
              cx={toScreen(hoverComponent.center).x}
              cy={toScreen(hoverComponent.center).y}
              r={12 * mk}
              fill="none"
              stroke="hsl(var(--destructive))"
              strokeWidth={1.5 * mk}
              strokeDasharray="3 3"
            />
            <text
              x={toScreen(hoverComponent.center).x + 14 * mk}
              y={toScreen(hoverComponent.center).y + 4}
              className="fill-destructive"
              style={{ fontSize: 10 * (coarse ? 1.3 : 1), fontFamily }}
            >
              TAP TO REMOVE
            </text>
          </g>
        )}

        {/* snap guides along every lockable axis */}
        {axisGuides.map((g) => (
          <g key={g.axis} opacity={g.active ? 0.9 : 0.32}>
            <line
              x1={g.from.x}
              y1={g.from.y}
              x2={g.to.x}
              y2={g.to.y}
              stroke="hsl(var(--primary))"
              strokeWidth={(g.active ? 1.2 : 0.8) * mk}
              strokeDasharray={g.active ? "2 6" : "1 7"}
            />
            {g.ticks.map((t, i) => (
              <circle key={i} cx={t.x} cy={t.y} r={(g.active ? 1.6 : 1) * mk} fill="hsl(var(--primary))" />
            ))}
          </g>
        ))}

        {/* vertices a branch can lock onto */}
        {branchVertices.map((v, i) => (
          <path
            key={i}
            d={`M ${v.x} ${v.y - 5 * mk} L ${v.x + 5 * mk} ${v.y} L ${v.x} ${v.y + 5 * mk} L ${v.x - 5 * mk} ${v.y} Z`}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={1 * mk}
            opacity={0.6}
          />
        ))}

        {/* live preview */}
        {lastPt && preview && (
          <g>
            {turnMarker && turnMarker.d && (
              <>
                <path
                  d={turnMarker.d}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth={1.2}
                />
                <text
                  x={turnMarker.at.x + 10}
                  y={turnMarker.at.y + 18}
                  className="fill-primary"
                  style={{ fontSize: 9 * (coarse ? 1.3 : 1), fontFamily }}
                >
                  {turnMarker.label}
                </text>
              </>
            )}
            <circle
              cx={toScreen(preview.to).x}
              cy={toScreen(preview.to).y}
              r={5 * mk}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5 * mk}
            />

            <line
              x1={toScreen(lastPt).x}
              y1={toScreen(lastPt).y}
              x2={toScreen(preview.to).x}
              y2={toScreen(preview.to).y}
              stroke="hsl(var(--primary))"
              strokeWidth={2 * mk}
              strokeDasharray="5 4"
            />
            <text
              x={(toScreen(lastPt).x + toScreen(preview.to).x) / 2}
              y={(toScreen(lastPt).y + toScreen(preview.to).y) / 2 - 8}
              textAnchor="middle"
              className="fill-primary"
              style={{ fontSize: 11 * (coarse ? 1.3 : 1), fontFamily }}
            >
              {formatFtIn(preview.inches)} · {axisLabel[preview.axis]} · {size}
              {allowedAxes.length < 6 ? " · 90° LOCK" : ""}
            </text>
          </g>
        )}

        {/* start cursor when nothing is being drawn */}
        {!lastPt && cursor && mode === "draw" && (
          <circle
            cx={toScreen(groundPoint(cursor.x, cursor.y)).x}
            cy={toScreen(groundPoint(cursor.x, cursor.y)).y}
            r={4 * mk}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={1.5 * mk}
          />
        )}

        {/* north arrow — standard ISO orientation marker */}
        <g
          transform={`translate(${box.w - 46} 34)`}
          stroke="hsl(var(--muted-foreground))"
          fill="none"
          strokeWidth={1.2}
        >
          <line x1={0} y1={0} x2={northDir.x * 24} y2={northDir.y * 24} />
          <path
            d={`M ${northDir.x * 24} ${northDir.y * 24} l ${-northDir.x * 8 + northDir.y * 4} ${
              -northDir.y * 8 - northDir.x * 4
            } l ${northDir.y * -8} ${northDir.x * 8} Z`}
            fill="hsl(var(--muted-foreground))"
            stroke="none"
          />
          <text
            x={northDir.x * 32}
            y={northDir.y * 32 + 3}
            textAnchor="middle"
            className="fill-muted-foreground"
            stroke="none"
            style={{ fontSize: 10, fontFamily }}
          >
            N
          </text>
        </g>
      </svg>

      {/* note entry */}
      {pendingNote && (
        <div
          className="absolute z-10 rounded-md border border-border bg-background p-2 shadow-lg"
          style={{
            left: Math.min(Math.max(pendingNote.screen.x, 8), Math.max(8, box.w - 220)),
            top: Math.min(Math.max(pendingNote.screen.y, 8), Math.max(8, box.h - 80)),
          }}
        >
          <input
            autoFocus
            value={pendingNote.text}
            placeholder="Note or spool tag…"
            onChange={(e) => setPendingNote({ ...pendingNote, text: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onAddNote(pendingNote.at, pendingNote.text);
                setPendingNote(null);
              }
              if (e.key === "Escape") setPendingNote(null);
            }}
            className="h-8 w-48 rounded border border-input bg-background px-2 text-xs"
          />
          <div className="mt-1 text-[10px] text-muted-foreground">Enter to place · Esc to cancel</div>
        </div>
      )}

      {/* dimension value entry — re-lengths the straight and everything downstream */}
      {dimEdit && (
        <div
          className="absolute z-10 rounded-md border border-border bg-background p-2 shadow-lg"
          style={{
            left: Math.min(Math.max(dimEdit.at.x - 60, 8), Math.max(8, box.w - 200)),
            top: Math.min(Math.max(dimEdit.at.y - 44, 8), Math.max(8, box.h - 84)),
          }}
        >
          <input
            autoFocus
            value={dimEdit.value}
            placeholder={`e.g. 4'-6" or 54`}
            aria-label="Dimension length"
            onChange={(e) => setDimEdit({ ...dimEdit, value: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const inches = parseLengthInches(dimEdit.value);
                if (inches && inches > 0) {
                  onSetSegmentLength?.(dimEdit.runId, dimEdit.index, inches);
                }
                setDimEdit(null);
              }
              if (e.key === "Escape") setDimEdit(null);
            }}
            onBlur={() => setDimEdit(null)}
            className="h-8 w-40 rounded border border-input bg-background px-2 font-mono text-xs"
          />
          <div className="mt-1 text-[10px] text-muted-foreground">
            Enter to apply · Esc to cancel · plain number = inches
          </div>
        </div>
      )}



      <div className="pointer-events-none absolute left-2 top-2 rounded-md bg-background/85 px-2 py-1 text-[10px] font-mono text-muted-foreground">
        {previewSpec.nps} {mat.sheetTag} · OD {previewSpec.od}" · {previewSpec.lbPerFt} lb/ft ·
        {" "}90° {previewSpec.elbow90}" · tee {previewSpec.teeRun}"
      </div>

      {/* symbol legend */}
      <div className="pointer-events-none absolute left-2 top-9 rounded-md bg-background/85 px-2 py-1 font-mono text-[9px] text-muted-foreground">
        <svg width={210} height={42} aria-hidden>
          <g stroke="hsl(var(--foreground))" strokeWidth={1.6} fill="none">
            <line x1={4} y1={11} x2={24} y2={11} />
            <circle cx={14} cy={11} r={2.6} fill="hsl(var(--foreground))" strokeWidth={0} />
            <path d="M 82 17 L 82 11 Q 82 5 88 5" />
            <line x1={134} y1={11} x2={154} y2={11} />
            <line x1={144} y1={11} x2={144} y2={4} />
            <path d="M 6 40 L 6 28 L 20 40 L 20 28 Z" fill="hsl(var(--background))" strokeWidth={1.2} />
            <line x1={13} y1={34} x2={13} y2={26} strokeWidth={1} />
          </g>
          <g className="fill-muted-foreground" style={{ fontSize: 8 }}>
            <text x={30} y={14}>{mat.jointType === "bw" ? "BW WELD" : "JOINT"}</text>
            <text x={94} y={14}>ELL</text>
            <text x={160} y={14}>TEE</text>
            <text x={26} y={37}>VALVE</text>
            <text x={70} y={37}>{mat.sheetTag}</text>
          </g>
        </svg>
      </div>

      {mode === "branch" && !activeRunId && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-mono text-primary">
          Branch mode — click on a pipe to tee off
        </div>
      )}
      {mode === "place" && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-mono text-primary">
          Place mode — click a pipe to drop the item
        </div>
      )}
      {mode === "note" && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-mono text-primary">
          Note mode — click the sheet to add text
        </div>
      )}
      {mode === "pan" && (
        <div className="pointer-events-none absolute right-2 top-2 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-mono text-primary">
          Pan mode — drag to move the view, nothing is drawn
        </div>
      )}

      {/* gesture help overlay */}
      {showHelp && (
        <div
          onPointerEnter={clearHelpTimer}
          className="absolute bottom-2 left-2 right-14 max-w-sm rounded-md border border-border bg-background/95 p-3 text-[11px] shadow-lg"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="font-semibold text-foreground">{modeHelp.title}</span>
            <button
              type="button"
              aria-label="Close gesture help"
              onClick={closeHelp}
              className="rounded px-1 text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>
          <ul className="space-y-0.5 text-muted-foreground">
            {modeHelp.lines.map((l) => (
              <li key={l} className="flex gap-1.5">
                <span className="text-primary">·</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="absolute bottom-2 right-2 flex flex-col gap-1">
        <button
          type="button"
          aria-label="Toggle dimension lines"
          aria-pressed={showDims}
          onClick={() => setShowDims((v) => !v)}
          className={`flex h-9 w-9 items-center justify-center rounded-md border border-border text-[10px] font-semibold shadow-sm active:scale-95 ${
            showDims ? "bg-primary text-primary-foreground" : "bg-background/90 text-foreground"
          }`}
        >
          DIM
        </button>
        <button
          type="button"
          aria-label="Toggle weld symbols"
          aria-pressed={showWelds}
          onClick={() => setShowWelds((v) => !v)}
          className={`flex h-9 w-9 items-center justify-center rounded-md border border-border text-[10px] font-semibold shadow-sm active:scale-95 ${
            showWelds ? "bg-primary text-primary-foreground" : "bg-background/90 text-foreground"
          }`}
        >
          BW
        </button>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => zoomBy(1.25)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/90 text-base font-semibold text-foreground shadow-sm active:scale-95"
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => zoomBy(0.8)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/90 text-base font-semibold text-foreground shadow-sm active:scale-95"
        >
          −
        </button>
        <button
          type="button"
          aria-label="Show gesture help"
          aria-expanded={showHelp}
          onClick={() => (showHelp ? closeHelp() : openHelp())}
          className={`flex h-9 w-9 items-center justify-center rounded-md border border-border text-base font-semibold shadow-sm active:scale-95 ${
            showHelp ? "bg-primary text-primary-foreground" : "bg-background/90 text-foreground"
          }`}
        >
          ?
        </button>
        <button
          type="button"
          aria-label="Fit drawing to view"
          onClick={fitView}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background/90 text-[10px] font-semibold text-foreground shadow-sm active:scale-95"
        >
          FIT
        </button>
      </div>
    </div>
  );
}
